import React from 'react';
import './Pages.css';

const AboutPage = () => {
  return (
    <div className="page-container">
      <div className="page-content">
        <h1 className="page-title">About MathLaTech</h1>
        
        <h2 className="section-title">Executable Mathematics for the Modern Age</h2>
        
        <p className="intro-text">
          Traditional mathematical notation, while elegant on paper, creates a disconnect between 
          mathematical thinking and computational reality. Students learn to manipulate symbols 
          they can't execute, leading to mathematics that feels abstract and impractical in our 
          computational world.
        </p>
        
        <div className="highlight-box">
          <strong>MathLaTech bridges this gap.</strong>
        </div>
        
        <h2 className="section-title">Introducing ./math</h2>
        
        <p>
          Our platform is powered by <strong>./math</strong>, a revolutionary high-level functional 
          composition language designed specifically for mathematical expression and computation.
        </p>
        
        <h3 className="subsection-title">How It Works</h3>
        
        <p>
          Instead of translating between mathematical notation and programming languages, ./math 
          allows you to write mathematics as executable functions:
        </p>
        
        <div className="code-example">
          <code>add(x, mul(x, div(x, y)))</code>
        </div>
        
        <p>This expression:</p>
        <ul className="feature-list">
          <li><strong>Renders</strong> as beautiful traditional mathematical notation: x + x × x/y</li>
          <li><strong>Executes</strong> immediately across multiple computational backends</li>
          <li><strong>Teaches</strong> compositional thinking and mathematical structure</li>
        </ul>
        
        <h3 className="subsection-title">The Power of Functional Composition</h3>
        
        <p>
          ./math treats all mathematical operations as function composition - the fundamental way 
          mathematicians actually think about relationships and transformations. This approach:
        </p>
        
        <ul className="feature-list">
          <li><strong>Eliminates translation gaps</strong> between mathematical concepts and computation</li>
          <li><strong>Makes mathematical structure explicit</strong> through clear function relationships</li>
          <li><strong>Enables seamless execution</strong> across different computational environments</li>
          <li><strong>Scales naturally</strong> to advanced mathematical concepts and domains</li>
        </ul>
        
        <div className="two-column">
          <div className="column">
            <h3 className="subsection-title">For Educators</h3>
            <p>MathLaTech provides a powerful pedagogical tool that helps students:</p>
            <ul>
              <li>Understand the computational nature of mathematical operations</li>
              <li>See immediate results from their mathematical expressions</li>
              <li>Bridge the gap between abstract math and practical application</li>
              <li>Develop computational thinking alongside mathematical reasoning</li>
            </ul>
          </div>
          
          <div className="column">
            <h3 className="subsection-title">For Students</h3>
            <p>Experience mathematics as a living, executable language where:</p>
            <ul>
              <li>Every expression can be computed and visualized</li>
              <li>Mathematical concepts connect directly to real-world computation</li>
              <li>Learning math means learning to think computationally</li>
              <li>Abstract notation becomes concrete and actionable</li>
            </ul>
          </div>
        </div>
        
        <h2 className="section-title">The Vision</h2>
        
        <p>
          ./math represents more than a tool - it's a new paradigm for mathematical literacy in the 
          computational age. By making mathematical notation directly executable while preserving 
          its traditional beauty, we're creating the foundation for how mathematics should be taught 
          and learned in the 21st century.
        </p>
        
        <div className="vision-statement">
          <strong>
            Mathematics isn't just about symbols on paper anymore. It's about computational thinking, 
            executable knowledge, and the power to turn mathematical ideas into immediate reality.
          </strong>
        </div>
        
        <div className="cta-section">
          <p><em>
            Ready to experience executable mathematics? Explore MathLaTech and discover how ./math 
            transforms the way you think about mathematical expression and computation.
          </em></p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;